import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrencies } from '../lib/api';

const FALLBACK_CURRENCIES = [
  { code: 'USD', name: 'US Dollar',          symbol: '$',  exchangeRate: 1 },
  { code: 'VND', name: 'Vietnamese Dong',    symbol: '₫',  exchangeRate: 25400 },
  { code: 'AUD', name: 'Australian Dollar',  symbol: 'A$', exchangeRate: 1.53 },
  { code: 'SGD', name: 'Singapore Dollar',   symbol: 'S$', exchangeRate: 1.35 },
  { code: 'EUR', name: 'Euro',               symbol: '€',  exchangeRate: 0.92 },
];

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState(FALLBACK_CURRENCIES);
  const [currency, setCurrency] = useState(
    () => localStorage.getItem('vietclinics_currency') || 'USD'
  );

  // Fetch latest rates from API on mount; fall back silently
  useEffect(() => {
    getCurrencies()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setCurrencies(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem('vietclinics_currency', currency);
  }, [currency]);

  const changeCurrency = (code) => {
    if (currencies.find((c) => c.code === code)) setCurrency(code);
  };

  const currentCurrency = currencies.find((c) => c.code === currency) || currencies[0];

  /**
   * Convert a USD price to the selected currency and format it.
   * VND: round to nearest 1000, no decimals.
   * Others: up to 2 decimals, trailing zeros stripped.
   */
  const formatPrice = (priceUsd) => {
    const num = parseFloat(priceUsd);
    if (!num && num !== 0) return '';
    const amount = num * (currentCurrency.exchangeRate || 1);
    if (currency === 'VND') {
      const rounded = Math.round(amount / 1000) * 1000;
      return `${currentCurrency.symbol}${rounded.toLocaleString()}`;
    }
    const fixed = parseFloat(amount.toFixed(2));
    const display = fixed % 1 === 0
      ? fixed.toLocaleString()
      : fixed.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return `${currentCurrency.symbol}${display}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, changeCurrency, formatPrice, currencies, currentCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within a CurrencyProvider');
  return context;
};

export default CurrencyContext;
